import type {
  ConsoleEntry,
  ConsoleLevel,
  NetworkEntry,
  PerformanceEntry,
  ErrorEntry,
  DiagnosticsSnapshot,
} from './types';

/**
 * Silent diagnostics capture module.
 *
 * Runs in the background from the moment the widget initialises.
 * Monkey-patches console, fetch, XHR, and installs global error handlers
 * to accumulate diagnostic data in circular buffers.
 *
 * None of this is visible to the end user.
 */
export class DiagnosticsCapture {
  private consoleBuffer: ConsoleEntry[] = [];
  private networkBuffer: NetworkEntry[] = [];
  private performanceBuffer: PerformanceEntry[] = [];
  private errorBuffer: ErrorEntry[] = [];

  private maxConsoleEntries: number;
  private maxNetworkEntries = 100;
  private maxPerformanceEntries = 100;
  private maxErrorEntries = 50;

  // Keep references to originals so we can restore on destroy
  private originalConsole: Record<ConsoleLevel, (...args: unknown[]) => void>;
  private originalFetch: typeof window.fetch;
  private originalXHROpen: typeof XMLHttpRequest.prototype.open;
  private originalXHRSend: typeof XMLHttpRequest.prototype.send;

  private performanceObserver?: PerformanceObserver;
  private teardownFns: Array<() => void> = [];

  constructor(maxConsoleEntries = 200) {
    this.maxConsoleEntries = maxConsoleEntries;

    // Stash originals
    this.originalConsole = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      debug: console.debug.bind(console),
      info: console.info.bind(console),
    };
    this.originalFetch = window.fetch.bind(window);
    this.originalXHROpen = XMLHttpRequest.prototype.open;
    this.originalXHRSend = XMLHttpRequest.prototype.send;
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────

  /** Start all capture hooks */
  start(): void {
    this.patchConsole();
    this.patchFetch();
    this.patchXHR();
    this.installErrorHandlers();
    this.installPerformanceObserver();
  }

  /** Stop all capture hooks and restore originals */
  destroy(): void {
    this.teardownFns.forEach((fn) => fn());
    this.teardownFns = [];
    this.performanceObserver?.disconnect();
    this.restoreConsole();
    this.restoreFetch();
    this.restoreXHR();
  }

  /** Return a snapshot of all captured diagnostics and clear the buffers */
  snapshot(): DiagnosticsSnapshot {
    const snap: DiagnosticsSnapshot = {
      consoleLog: [...this.consoleBuffer],
      networkLog: [...this.networkBuffer],
      performanceLog: [...this.performanceBuffer],
      errors: [...this.errorBuffer],
    };
    return snap;
  }

  // ─── Console patching ──────────────────────────────────────────────────

  private patchConsole(): void {
    const levels: ConsoleLevel[] = ['log', 'warn', 'error', 'debug', 'info'];

    for (const level of levels) {
      const original = this.originalConsole[level];
      console[level] = (...args: unknown[]) => {
        this.pushToBuffer(this.consoleBuffer, this.maxConsoleEntries, {
          level,
          message: args
            .map((a) => {
              try {
                return typeof a === 'string' ? a : JSON.stringify(a);
              } catch {
                return String(a);
              }
            })
            .join(' '),
          timestamp: Date.now(),
          stack: level === 'error' ? new Error().stack : undefined,
        });
        // Still call the original so devtools work normally
        original(...args);
      };
    }
  }

  private restoreConsole(): void {
    const levels: ConsoleLevel[] = ['log', 'warn', 'error', 'debug', 'info'];
    for (const level of levels) {
      console[level] = this.originalConsole[level];
    }
  }

  // ─── Fetch patching ────────────────────────────────────────────────────

  private patchFetch(): void {
    const self = this;
    window.fetch = async function (
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> {
      const url =
        input instanceof Request
          ? input.url
          : input instanceof URL
            ? input.toString()
            : input;
      const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
      const startTime = Date.now();

      try {
        const response = await self.originalFetch(input, init);
        const duration = Date.now() - startTime;

        // Only log failed requests (4xx/5xx) to keep buffer lean
        if (response.status >= 400) {
          let responseSnippet: string | undefined;
          try {
            const cloned = response.clone();
            const text = await cloned.text();
            responseSnippet = text.slice(0, 500);
          } catch {
            // Ignore — some responses can't be read
          }

          self.pushToBuffer(self.networkBuffer, self.maxNetworkEntries, {
            method: method.toUpperCase(),
            url,
            status: response.status,
            statusText: response.statusText,
            duration,
            timestamp: startTime,
            responseSnippet,
          });
        }

        return response;
      } catch (err) {
        const duration = Date.now() - startTime;
        self.pushToBuffer(self.networkBuffer, self.maxNetworkEntries, {
          method: method.toUpperCase(),
          url,
          status: 0,
          statusText: err instanceof Error ? err.message : 'Network Error',
          duration,
          timestamp: startTime,
        });
        throw err;
      }
    };
  }

  private restoreFetch(): void {
    window.fetch = this.originalFetch;
  }

  // ─── XHR patching ──────────────────────────────────────────────────────

  private patchXHR(): void {
    const self = this;

    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      (this as XMLHttpRequest & { _fhMethod: string; _fhUrl: string })._fhMethod = method;
      (this as XMLHttpRequest & { _fhUrl: string })._fhUrl =
        url instanceof URL ? url.toString() : url;
      return self.originalXHROpen.apply(this, [method, url, ...rest] as Parameters<typeof XMLHttpRequest.prototype.open>);
    };

    XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
      const xhr = this as XMLHttpRequest & { _fhMethod: string; _fhUrl: string };
      const startTime = Date.now();

      const onLoadEnd = () => {
        const duration = Date.now() - startTime;
        if (xhr.status >= 400 || xhr.status === 0) {
          self.pushToBuffer(self.networkBuffer, self.maxNetworkEntries, {
            method: (xhr._fhMethod ?? 'GET').toUpperCase(),
            url: xhr._fhUrl ?? '',
            status: xhr.status,
            statusText: xhr.statusText,
            duration,
            timestamp: startTime,
            responseSnippet: xhr.responseText?.slice(0, 500),
          });
        }
        xhr.removeEventListener('loadend', onLoadEnd);
      };

      xhr.addEventListener('loadend', onLoadEnd);
      return self.originalXHRSend.call(this, body);
    };
  }

  private restoreXHR(): void {
    XMLHttpRequest.prototype.open = this.originalXHROpen;
    XMLHttpRequest.prototype.send = this.originalXHRSend;
  }

  // ─── Global error handlers ─────────────────────────────────────────────

  private installErrorHandlers(): void {
    const onError = (event: ErrorEvent) => {
      this.pushToBuffer(this.errorBuffer, this.maxErrorEntries, {
        message: event.message,
        source: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        timestamp: Date.now(),
        type: 'error',
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      this.pushToBuffer(this.errorBuffer, this.maxErrorEntries, {
        message: reason instanceof Error ? reason.message : String(reason),
        stack: reason instanceof Error ? reason.stack : undefined,
        timestamp: Date.now(),
        type: 'unhandledrejection',
      });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    this.teardownFns.push(() => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    });
  }

  // ─── Performance observer ──────────────────────────────────────────────

  private installPerformanceObserver(): void {
    if (typeof PerformanceObserver === 'undefined') return;

    try {
      this.performanceObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'longtask' && entry.duration > 50) {
            this.pushToBuffer(this.performanceBuffer, this.maxPerformanceEntries, {
              type: 'long-task',
              name: entry.name,
              duration: entry.duration,
              startTime: entry.startTime,
              timestamp: Date.now(),
            });
          } else if (entry.entryType === 'resource') {
            // Only log slow resources (>2s)
            if (entry.duration > 2000) {
              this.pushToBuffer(this.performanceBuffer, this.maxPerformanceEntries, {
                type: 'resource',
                name: entry.name,
                duration: entry.duration,
                startTime: entry.startTime,
                timestamp: Date.now(),
              });
            }
          }
        }
      });

      // Observe what's available
      const supportedTypes = PerformanceObserver.supportedEntryTypes ?? [];
      const typesToObserve = ['longtask', 'resource'].filter((t) =>
        supportedTypes.includes(t),
      );

      if (typesToObserve.length > 0) {
        this.performanceObserver.observe({ entryTypes: typesToObserve });
      }
    } catch {
      // PerformanceObserver not supported — degrade gracefully
    }
  }

  // ─── Helpers ────────────────────────────────────────────────────────────

  private pushToBuffer<T>(buffer: T[], maxSize: number, entry: T): void {
    buffer.push(entry);
    if (buffer.length > maxSize) {
      buffer.splice(0, buffer.length - maxSize);
    }
  }
}
